/**
 * Journeys, across the portfolio.
 *
 * The design gives journeys a tab of their own, and until now there was no
 * screen behind that idea: a journey could only be reached by first choosing a
 * project, which answers "how is this project progressing?" and never "which
 * journey is furthest from done?".
 *
 * It is a list rather than a map on purpose. Five journeys drawn side by side
 * is five maps nobody can compare; the comparison people actually make is
 * position against remaining, and that is a number.
 */
import { Link } from 'react-router-dom';
import type { ProjectTile } from '@dcc/shared';
import { api } from '../lib/api';
import { useProjection } from '../hooks/useProjection';
import { StateChip } from '../components/Primitives';
import { EmptyState, ErrorState, SkeletonTiles } from '../components/States';

function Row({ tile }: { tile: ProjectTile }) {
  const remaining = tile.totalNodes - tile.completedNodes;
  return (
    <Link
      to={`/projects/${tile.id}/journey`}
      className="card card--interactive journeyrow"
      style={{ display: 'block' }}
    >
      <div className="spread">
        <strong>{tile.name}</strong>
        <StateChip kind="project" state={tile.status} />
      </div>

      <div className="journeyrow__bar" aria-hidden="true">
        <span style={{ width: `${Math.max(0, Math.min(100, tile.journeyCompletionPercent))}%` }} />
      </div>

      <div className="spread small dim" style={{ marginTop: 6 }}>
        <span className="mono">
          {tile.journeyCompletionPercent}% · {tile.completedNodes}/{tile.totalNodes}
        </span>
        <span>
          {/* Zero stations is not "finished", it is "no plan applied yet", and
              saying 100% there would be the most misleading number on the page. */}
          {tile.totalNodes === 0
            ? 'no journey planned'
            : remaining === 0
              ? 'every station passed'
              : `${remaining} station${remaining === 1 ? '' : 's'} remaining`}
        </span>
      </div>

      <p className="small" style={{ marginTop: 6 }}>
        {tile.nextStationLabel ? (
          <>
            Next: <strong>{tile.nextStationLabel}</strong>
          </>
        ) : (
          <span className="dim">Nothing scheduled next.</span>
        )}
      </p>
    </Link>
  );
}

export function Journeys() {
  const projection = useProjection(() => api.portfolio(), []);

  if (projection.loading) return <SkeletonTiles />;
  if (projection.error) return <ErrorState error={projection.error} onRetry={projection.reload} />;
  const view = projection.data!;

  // Furthest from done first: the question this screen exists to answer.
  const ordered = [...view.projects].sort(
    (a, b) => a.journeyCompletionPercent - b.journeyCompletionPercent,
  );

  return (
    <>
      <header className="topstrip">
        <div>
          <div className="label">{view.tenant.name}</div>
          <h1 className="topstrip__title">Journeys</h1>
        </div>
        <div className="topstrip__meta small dim">
          Ordered by how far each journey still has to go.
        </div>
      </header>

      {ordered.length === 0 ? (
        <EmptyState
          title="No journeys yet"
          body="A journey appears once a project has a plan applied to it."
        />
      ) : (
        <div className="stack">
          {ordered.map((tile) => (
            <Row key={tile.id} tile={tile} />
          ))}
        </div>
      )}
    </>
  );
}
